<script lang="ts">
  import { z } from 'zod'
  import { validatedForm } from '../../../hooks/forms'
  import { shareProject } from '../../../api/projects'
  import { getContext } from 'svelte'
  import i18n from '../../../i18n'
  import Input from '../../../components/input/Input.svelte'
  import Button from '../../../components/button/Button.svelte'
  import Modal from '../../../components/modal/Modal.svelte'
  import type { Writable } from 'svelte/store'

  type Props = {
    open?: boolean
  }

  let { open = $bindable(false) }: Props = $props()

  const projectId = getContext<Writable<string>>('projectId')

  const shareSchema = z.object({
    targetUserEmail: z.email(),
    note: z.string().max(1000).optional(),
  })

  const { onSubmit, formValues, errors, status } = validatedForm({
    zodSchema: shareSchema,
    submitFunction: project => shareProject($projectId, { project }),
    onError: (_err, _values, setError) => {
      console.error(_err)
      setError('targetUserEmail', $i18n.t('errors.unknown'))
    },
  })
</script>

<Modal
  bind:open
  onsubmit={onSubmit}
  title={$i18n.t('project.shareModal.title')}
>
  {#snippet body()}
    {#if $status !== 'success'}
      <p class="instructions">
        {$i18n.t('project.shareModal.instructions1')}
      </p>
      <p class="instructions">
        {$i18n.t('project.shareModal.instructions2')}
      </p>
      <div class="form-container">
        <Input
          autocomplete="off"
          data-testid="email"
          disabled={$status === 'submitting'}
          error={$errors.targetUserEmail}
          label={$i18n.t('form.emailLabel')}
          placeholder={$i18n.t('form.emailPlaceholder')}
          type="email"
          bind:value={$formValues.targetUserEmail}
        />
        <div class="note-field">
          <label for="share-note">{$i18n.t('project.shareModal.noteLabel')}</label>
          <textarea
            id="share-note"
            data-testid="note"
            disabled={$status === 'submitting'}
            maxlength={1000}
            placeholder={$i18n.t('project.shareModal.notePlaceholder')}
            rows={3}
            bind:value={$formValues.note}
          ></textarea>
        </div>
      </div>
    {:else}
      <p class="instructions">
        {$i18n.t('project.shareModal.successMessage', {
          userEmail: $formValues.targetUserEmail,
        })}
      </p>
    {/if}
  {/snippet}
  {#snippet footer()}
    {#if $status !== 'success'}
      <Button onclick={() => (open = false)} type="button" variant="ghost">
        {$i18n.t('globals.cancel')}
      </Button>
      <Button disabled={$status === 'submitting'} type="submit">
        {$i18n.t('project.shareModal.button')}
      </Button>
    {:else}
      <Button onclick={() => (open = false)}>
        {$i18n.t('project.shareModal.close')}
      </Button>
    {/if}
  {/snippet}
</Modal>

<style lang="scss">
  .instructions {
    color: var(--foreground-muted);
    font-size: 1rem;
    line-height: 1.5rem;
  }

  .form-container {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .note-field {
    align-items: flex-start;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
      color: var(--foreground);
      font-size: 0.75rem;
      line-height: 150%;
    }

    textarea {
      align-self: stretch;
      background: var(--input-background);
      border: none;
      border-radius: 0.5rem;
      color: var(--input-foreground);
      font-family: inherit;
      font-size: inherit;
      outline: none;
      padding: 0.75rem 1rem;
      resize: vertical;

      &::placeholder {
        color: var(--input-placeholder);
      }

      &:focus-visible {
        outline: 1px solid var(--stroke);
        outline-offset: 2px;
      }

      &:hover {
        background-color: var(--input-hover-background);
      }

      &:disabled {
        opacity: 0.35;
      }
    }
  }
</style>
